'use client';

import { ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface NavigationBarProps {
  currentStep: number;
  canProceed: boolean;
  isLoading: boolean;
  onNext: () => void;
  onBack: () => void;
  nextLabel: string;
  backLabel: string;
  loadingLabel: string;
  showBack: boolean;
  hidden: boolean;
  isKeyboardOpen: boolean;
  /** Step label for mobile progress indicator (e.g. "Your details") */
  stepLabel?: string;
  /** Skip handler — shown as ghost button next to primary CTA on step 4 */
  onSkip?: () => void;
  /** Desktop label for skip button (e.g. "Skip & Complete") */
  skipLabel?: string;
  /** Mobile label for skip button — shorter for tight space (e.g. "Skip") */
  skipLabelMobile?: string;
}

export function NavigationBar({
  currentStep,
  canProceed,
  isLoading,
  onNext,
  onBack,
  nextLabel,
  backLabel,
  loadingLabel,
  showBack,
  hidden,
  isKeyboardOpen,
  stepLabel,
  onSkip,
  skipLabel,
  skipLabelMobile,
}: NavigationBarProps) {
  if (hidden) return null;

  return (
    <>
      {/* Desktop */}
      <div className="hidden md:mt-6 md:-mx-12 md:flex md:items-center md:justify-between md:border-t md:border-stone-800 md:px-12 md:pt-3 lg:mx-0 lg:bg-transparent lg:px-0 lg:pt-8">
        {showBack ? (
          <Button
            type="button"
            variant="ghost"
            onClick={(e) => {
              e.preventDefault();
              onBack();
            }}
            className="text-stone-400 hover:bg-stone-800 hover:text-white"
          >
            {backLabel}
          </Button>
        ) : (
          <div />
        )}

        {currentStep < 4 ? (
          <Button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onNext();
            }}
            disabled={!canProceed || isLoading}
            className="bg-dgr-gold px-8 text-stone-950 hover:bg-dgr-gold-light disabled:cursor-not-allowed disabled:opacity-50"
          >
            {nextLabel}
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        ) : (
          <div className="flex items-center gap-3">
            {onSkip && skipLabel && (
              <Button
                type="button"
                onClick={onSkip}
                disabled={!canProceed || isLoading}
                className="border border-stone-700 bg-transparent px-6 text-stone-400 hover:bg-stone-800 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {skipLabel}
              </Button>
            )}
            <Button
              type="button"
              disabled={!canProceed || isLoading}
              onClick={onNext}
              className="bg-dgr-gold px-8 text-stone-950 hover:bg-dgr-gold-light disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {loadingLabel}
                </span>
              ) : (
                nextLabel
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Mobile */}
      <div
        className={`fixed inset-x-0 bottom-0 z-40 flex flex-col border-t border-stone-800 bg-stone-950/95 px-4 pt-3 pb-safe backdrop-blur sm:px-6 md:hidden ${isKeyboardOpen ? 'gap-2' : 'gap-2.5'}`}
      >
        {/* Progress bar */}
        {stepLabel && (
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium uppercase tracking-[0.18em] text-stone-500">
              {currentStep}/4
            </span>
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-stone-800">
              <div
                className="h-full rounded-full bg-dgr-gold transition-all duration-300"
                style={{ width: `${(currentStep / 4) * 100}%` }}
              />
            </div>
            <span className="truncate text-xs text-stone-400">{stepLabel}</span>
          </div>
        )}

        {/* Buttons — single row */}
        <div className="flex items-center gap-2.5">
          {showBack ? (
            <Button
              type="button"
              variant="ghost"
              onClick={(e) => {
                e.preventDefault();
                onBack();
              }}
              className="shrink-0 px-4 text-stone-400 hover:bg-stone-800 hover:text-white"
            >
              {backLabel}
            </Button>
          ) : null}

          {currentStep < 4 ? (
            <Button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onNext();
              }}
              disabled={!canProceed || isLoading}
              className="min-w-0 flex-1 justify-center whitespace-normal bg-dgr-gold px-6 py-3 text-center text-stone-950 hover:bg-dgr-gold-light disabled:cursor-not-allowed disabled:opacity-50"
            >
              {nextLabel}
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <>
              {onSkip && skipLabelMobile && (
                <Button
                  type="button"
                  onClick={onSkip}
                  disabled={!canProceed || isLoading}
                  className="shrink-0 border border-stone-700 bg-transparent px-4 text-stone-400 hover:bg-stone-800 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {skipLabelMobile}
                </Button>
              )}
              <Button
                type="button"
                disabled={!canProceed || isLoading}
                onClick={onNext}
                className="min-w-0 flex-1 justify-center whitespace-normal bg-dgr-gold px-6 py-3 text-center text-stone-950 hover:bg-dgr-gold-light disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {loadingLabel}
                  </span>
                ) : (
                  nextLabel
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
